import { useEffect, useState } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import {
  LogOut, LayoutDashboard, LayoutList, Users, Building2, FileText, CalendarDays,
  Truck, ClipboardList, AlertTriangle, Wallet, Receipt, FolderOpen, Mail, Zap,
  Settings, ScrollText, Bell, Menu, X, UsersRound, Smartphone,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAdminAuth } from "@/context/AdminAuthContext";
import { apiClient } from "@/lib/api";

const NAV_GROUPS = [
  { label: "Pilotage", items: [
    { to: "/admin/tableau-de-bord", label: "Tableau de bord", icon: LayoutDashboard, testId: "nav-dashboard" },
  ]},
  { label: "Commercial", items: [
    { to: "/admin", label: "Dossiers", icon: LayoutList, testId: "nav-cases", exact: true },
    { to: "/admin/contacts", label: "Contacts", icon: Users, testId: "nav-contacts" },
    { to: "/admin/organisations", label: "Organisations", icon: Building2, testId: "nav-organizations" },
    { to: "/admin/devis", label: "Devis", icon: FileText, testId: "nav-quotes" },
  ]},
  { label: "Exploitation", items: [
    { to: "/admin/planning", label: "Planning", icon: CalendarDays, testId: "nav-planning", perm: "mission_plan" },
    { to: "/admin/missions", label: "Missions", icon: Truck, testId: "nav-missions" },
    { to: "/admin/terrain", label: "Terrain", icon: Smartphone, testId: "nav-field", perm: "field_update" },
    { to: "/admin/taches", label: "Tâches", icon: ClipboardList, testId: "nav-tasks" },
    { to: "/admin/incidents", label: "Incidents", icon: AlertTriangle, testId: "nav-incidents" },
    { to: "/admin/vehicules", label: "Véhicules", icon: Truck, testId: "nav-vehicles" },
    { to: "/admin/equipes", label: "Équipes", icon: UsersRound, testId: "nav-teams" },
  ]},
  { label: "Finance", items: [
    { to: "/admin/paiements", label: "Paiements", icon: Wallet, testId: "nav-payments" },
    { to: "/admin/factures", label: "Factures", icon: Receipt, testId: "nav-invoices" },
    { to: "/admin/documents", label: "Documents", icon: FolderOpen, testId: "nav-documents" },
  ]},
  { label: "Marketing", items: [
    { to: "/admin/communications", label: "Communications", icon: Mail, testId: "nav-communications" },
    { to: "/admin/automatisations", label: "Automatisations", icon: Zap, testId: "nav-automations", perm: "automation_manage" },
  ]},
  { label: "Administration", items: [
    { to: "/admin/parametres", label: "Paramètres", icon: Settings, testId: "nav-settings", perm: "settings_manage" },
    { to: "/admin/journal", label: "Journal", icon: ScrollText, testId: "nav-audit", perm: "audit_view" },
  ]},
];

let metaCache = null;

export function AdminGuard({ children }) {
  const { admin } = useAdminAuth();
  if (admin === null) return <div className="min-h-screen flex items-center justify-center"><span className="text-navy text-sm">Chargement…</span></div>;
  if (admin === false) { window.location.href = "/admin/login"; return null; }
  return <AdminShell>{children}</AdminShell>;
}

export default function AdminShell({ children }) {
  const { logout } = useAdminAuth();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [perms, setPerms] = useState(metaCache?.my_permissions || null);
  const [unread, setUnread] = useState(0);
  const [showNotifs, setShowNotifs] = useState(false);
  const [notifs, setNotifs] = useState([]);

  useEffect(() => {
    if (!metaCache) {
      apiClient.get("/admin/crm-meta").then((r) => { metaCache = r.data; setPerms(r.data.my_permissions); }).catch(() => setPerms([]));
    }
    apiClient.get("/admin/notifications", { params: { unread_only: true, page_size: 30 } })
      .then((r) => { setUnread(r.data.unread); setNotifs(r.data.items); }).catch(() => {});
  }, [pathname]);

  const can = (perm) => !perm || !perms || perms.includes(perm);

  const markAllRead = async () => {
    await apiClient.post("/admin/notifications/read-all").catch(() => {});
    setUnread(0); setNotifs([]);
  };

  const NavItems = ({ onNav }) => (
    <nav className="flex-1 overflow-y-auto py-3">
      {NAV_GROUPS.map((g) => {
        const visible = g.items.filter((i) => can(i.perm));
        if (!visible.length) return null;
        return (
          <div key={g.label} className="mb-3">
            <div className="px-4 text-[10px] uppercase tracking-widest text-white/40 font-bold mb-1">{g.label}</div>
            {visible.map((t) => {
              const active = t.exact ? pathname === t.to : pathname.startsWith(t.to);
              return (
                <NavLink key={t.to} to={t.to} end={t.exact} data-testid={t.testId} onClick={onNav}
                  className={`flex items-center gap-2.5 px-4 py-2 text-sm font-medium transition-colors ${
                    active ? "text-white bg-white/10 border-l-2 border-orange" : "text-white/60 hover:text-white hover:bg-white/5 border-l-2 border-transparent"}`}>
                  <t.icon className="w-4 h-4 shrink-0" />{t.label}
                </NavLink>
              );
            })}
          </div>
        );
      })}
    </nav>
  );

  return (
    <div className="min-h-screen bg-graylight flex">
      <aside className="hidden lg:flex flex-col w-60 bg-navy shrink-0 sticky top-0 h-screen">
        <div className="px-4 py-4 font-heading font-bold text-white text-base border-b border-white/10">Express Delivery — CRM</div>
        <NavItems />
        <button data-testid="admin-logout-button" onClick={logout}
          className="flex items-center gap-2 px-4 py-3 text-sm text-white/60 hover:text-white border-t border-white/10">
          <LogOut className="w-4 h-4" /> Déconnexion
        </button>
      </aside>

      {open && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="absolute inset-0 bg-black/50" onClick={() => setOpen(false)} />
          <aside className="absolute left-0 top-0 bottom-0 w-64 bg-navy flex flex-col">
            <div className="px-4 py-4 flex items-center justify-between border-b border-white/10">
              <span className="font-heading font-bold text-white text-sm">Express Delivery</span>
              <button data-testid="mobile-menu-close" onClick={() => setOpen(false)} className="text-white/70"><X className="w-5 h-5" /></button>
            </div>
            <NavItems onNav={() => setOpen(false)} />
            <button onClick={logout} className="flex items-center gap-2 px-4 py-3 text-sm text-white/60 border-t border-white/10">
              <LogOut className="w-4 h-4" /> Déconnexion
            </button>
          </aside>
        </div>
      )}

      <div className="flex-1 min-w-0 flex flex-col">
        <header className="bg-white border-b border-gray-200 px-4 py-2.5 flex items-center gap-3 sticky top-0 z-40">
          <button data-testid="mobile-menu-open" onClick={() => setOpen(true)} className="lg:hidden text-navy"><Menu className="w-5 h-5" /></button>
          <div className="flex-1" />
          <div className="relative">
            <button data-testid="notifications-bell" onClick={() => setShowNotifs(!showNotifs)}
              className="relative text-navy hover:text-orange p-1.5">
              <Bell className="w-5 h-5" />
              {unread > 0 && <span data-testid="notifications-badge" className="absolute -top-0.5 -right-0.5 bg-orange text-white text-[10px] font-bold rounded-full min-w-[16px] h-4 px-0.5 flex items-center justify-center">{unread}</span>}
            </button>
            {showNotifs && (
              <div data-testid="notifications-panel" className="absolute right-0 mt-2 w-80 bg-white rounded-xl shadow-xl border border-gray-200 z-50 max-h-96 overflow-y-auto">
                <div className="flex items-center justify-between px-3 py-2 border-b">
                  <span className="text-sm font-bold text-navy">Notifications</span>
                  <Button data-testid="notifications-read-all" variant="ghost" size="sm" onClick={markAllRead} className="text-xs h-7">Tout marquer lu</Button>
                </div>
                {notifs.length === 0 ? (
                  <div className="px-3 py-6 text-center text-sm text-muted-foreground">Aucune notification non lue</div>
                ) : notifs.map((n) => (
                  <button key={n.id} className="w-full text-left px-3 py-2.5 hover:bg-gray-50 border-b last:border-0"
                    onClick={() => { setShowNotifs(false); if (n.link) navigate(n.link); }}>
                    <div className="text-sm font-semibold text-navy">{n.title}</div>
                    {n.message && <div className="text-xs text-muted-foreground line-clamp-2">{n.message}</div>}
                  </button>
                ))}
              </div>
            )}
          </div>
        </header>
        <main className="flex-1 px-4 md:px-6 py-5 max-w-[1500px] w-full mx-auto">{children}</main>
      </div>
    </div>
  );
}
